import * as PropTypes from "prop-types";

import { Component } from "react";
import { FormattedMessage, injectIntl } from "react-intl";
import ObjectUtils, { coalesce } from "../../../../utils/ObjectUtils";

import { faFileCsv, faFileUpload, faPlus, faTrash } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import classNames from "classnames/bind";
import { ButtonGroup, ButtonToolbar } from "react-bootstrap";
import Button from "react-bootstrap/Button";
import Col from "react-bootstrap/Col";
import Row from "react-bootstrap/Row";
import BootstrapTable from "react-bootstrap/Table";
import { getIncompleteColumns, getRequiredColumns, isAnswered } from "../../../../utils/QuestionUtils";
import CsvFileUploadModalDialogContainer from "./dynamicTableFileUpload/CsvFileUploadModalDialogContainer";
import QuestionHeader from "./QuestionHeader";
import TableQuestionBody from "./TableQuestionBody";
import TableQuestionDialog from "./TableQuestionDialog";
import TableQuestionHead from "./TableQuestionHead";

const { findByObjectProp, opt, getDeepProperty } = ObjectUtils;

/**
 * Table question component for survey-view.
 */
class TableQuestion extends Component {
  constructor(props) {
    super(props);
    this.state = {
      showFileUpload: false,
      showEditDialog: false,
      rowIndex: undefined,
      monitoringEditOnly: false,
      textExpanded: false,
      isNewRow: false
    };

    this.showFileUpload = this.showFileUpload.bind(this);
    this.hideFileUpload = this.hideFileUpload.bind(this);
    this.handleAddRow = this.handleAddRow.bind(this);
    this.handleShowEditDialog = this.handleShowEditDialog.bind(this);
    this.handleCloseEditDialog = this.handleCloseEditDialog.bind(this);
    this.removeActivityQuestion = this.removeActivityQuestion.bind(this);
  }

  handleShowEditDialog(rowIndex, monitoringEditOnly, isNewRow = false) {
    this.setState({
      showEditDialog: true,
      rowIndex,
      monitoringEditOnly,
      isNewRow
    });
  }

  handleCloseEditDialog() {
    this.setState({
      showEditDialog: false,
      rowIndex: undefined
    });
  }

  handleAddRow() {
    const { question, questionAnswer, addRow } = this.props;

    addRow(question);

    this.handleShowEditDialog(questionAnswer.rows ? questionAnswer.rows.length - 1 : 0, false, true);
  }

  isFirstRowEmpty() {
    const { question } = this.props;
    let result = true;
    if (question.type !== "dynamicTable") {
      let rows = coalesce(getDeepProperty(question, "rows"), []);
      rows.forEach((row) => {
        result = result && coalesce(row.text, "") === "";
      });
    }
    return result;
  }

  showFileUpload() {
    this.setState({
      showFileUpload: true
    });
  }

  hideFileUpload() {
    this.setState({
      showFileUpload: false
    });
  }

  downloadCSVTemplate(question) {
    this.props.downloadCSVTemplate(question);
  }

  mergeRows(rows, answerRows) {
    const result = [];
    rows.forEach((row) => {
      const mergedRow = Object.assign({}, row, { uid: opt(findByObjectProp(answerRows, "id", row.name)).uid });
      result.push(mergedRow);
    });
    return result;
  }

  removeActivityQuestion(question) {
    // first we match on the activityList column type to identify the correct table for styling purposes
    const expanded = question.columns.find((col) => col.type === "activityList") ? true : false;
    if (this.state.textExpanded !== expanded) {
      this.setState({ textExpanded: expanded });
    }

    // then we actually filter the activityList column out
    const columns = question.columns.filter((col) => col.type !== "activityList");
    let newQuestion = ObjectUtils.deepClone(question);
    newQuestion.columns = columns;
    return newQuestion;
  }

  render() {
    let {
      question,
      surveyTemplate,
      questionAnswer,
      surveyResult,
      deleteRow,
      importRows,
      disabled,
      monitoringEditOnly,
      intl
    } = this.props;

    const checkedQuestion = this.removeActivityQuestion(question);

    const skipFirst = this.isFirstRowEmpty();

    const isDynamicTable = question.type === "dynamicTable";
    const rows = isDynamicTable
      ? coalesce(questionAnswer.rows, [])
      : this.mergeRows(question.rows, coalesce(questionAnswer.rows, []));

      if (rows.length === 0 && isDynamicTable && this.state.showEditDialog) {
        this.handleCloseEditDialog();
      }

    const answered = isAnswered(question, questionAnswer);
    const incompleteColumns = getIncompleteColumns(question.columns, questionAnswer, rows);
    const requiredColumns = getRequiredColumns(question.columns);
    const requiredColumnsAnswered = answered && incompleteColumns.length === 0;
    return (
      <Row>
        <Col>
          <QuestionHeader question={question} answered={requiredColumnsAnswered}>
            {isDynamicTable && !requiredColumnsAnswered && questionAnswer.rows && questionAnswer.rows.length > 0 && incompleteColumns.length > 0 &&
              <div className="text-danger">
                <FormattedMessage 
                  id="user.surveyView.table.mandatory.columns" 
                  values={{
                    completeColumns: requiredColumns.length - incompleteColumns.length,
                    columnLength: requiredColumns.length,
                  }}
                />

                <ul className="list-unstyled">
                  {incompleteColumns.map(column =>  <li>-&emsp;{column.text}</li>)}
                </ul>
              </div>
            }

            {isDynamicTable ? (
              <div className="dynamic-table border">
                {this.renderTable(
                  isDynamicTable,
                  checkedQuestion,
                  skipFirst,
                  disabled,
                  monitoringEditOnly,
                  rows,
                  questionAnswer,
                  deleteRow
                )}
              </div>
            ) : (
              this.renderTable(
                isDynamicTable,
                checkedQuestion,
                skipFirst,
                disabled,
                monitoringEditOnly,
                rows,
                questionAnswer,
                deleteRow
              )
            )}
          </QuestionHeader>

          {rows.length === 0 && (
            <div className="text-center mt-2" onClick={this.handleAddRow} style={{ cursor: "pointer" }}>
               <FormattedMessage id="user.surveyView.table.rows.empty" />
            </div>
          )}

          {isDynamicTable && (
            <ButtonToolbar className="mt-2">
              <ButtonGroup>
                <Button variant="primary" disabled={disabled} onClick={this.handleAddRow}>
                  <FontAwesomeIcon icon={faPlus} className="me-2" />
                  <FormattedMessage id="user.surveyView.table.addRow" />
                </Button>
                
                <Button disabled={disabled} variant="secondary" onClick={() => this.props.deleteRow(question)}>
                  <FontAwesomeIcon icon={faTrash} className="me-2" />
                  <FormattedMessage id="user.surveyView.table.deleteRows" />
                </Button>

                {question.enableUpload && (
                  <>
                    <Button variant="secondary" disabled={disabled} onClick={() => this.downloadCSVTemplate(question)}>
                      <FontAwesomeIcon icon={faFileCsv} className="me-2" />
                      <FormattedMessage id="user.surveyView.table.downloadExcel" />
                    </Button>
                    <Button
                      variant="secondary"
                      title={intl.formatMessage({ id: "user.surveyView.table.downloadExcel.hint" })}
                      disabled={disabled}
                      onClick={this.showFileUpload}
                    >
                      <FontAwesomeIcon icon={faFileUpload} className="me-2" />
                      <FormattedMessage id="user.surveyView.table.uploadCSV" />
                    </Button>
                  </>
                )}
                <CsvFileUploadModalDialogContainer
                  onClose={this.hideFileUpload}
                  show={this.state.showFileUpload}
                  importRows={importRows}
                  question={question}
                  answer={questionAnswer}
                />
              </ButtonGroup>
            </ButtonToolbar>
          )}

          {this.state.showEditDialog && (
            <TableQuestionDialog
              rows={rows}
              surveyTemplate={surveyTemplate}
              surveyResult={surveyResult}
              rowIndex={this.state.rowIndex}
              question={question}
              monitoringEditOnly={monitoringEditOnly}
              questionAnswer={questionAnswer}
              open={this.state.showEditDialog}
              onClose={this.handleCloseEditDialog}
              isNewRow={this.state.isNewRow}
              deleteRows={this.props.deleteRows}
              validation={this.props.validation}
              addRow={this.handleAddRow}
              deleteRow={this.props.deleteRow}
              addQuestionAnswer={this.props.addQuestionAnswer}
              setQuestionAnswer={this.props.setQuestionAnswer}
              removeQuestionAnswer={this.props.removeQuestionAnswer}
              validateQuestionAnswer={this.props.validateQuestionAnswer}
              wideScreen={this.state.textExpanded}
            />
          )}
        </Col>
      </Row>
    );
  }

  renderTable(
    isDynamicTable,
    checkedQuestion,
    skipFirst,
    disabled,
    monitoringEditOnly,
    rows,
    questionAnswer,
    deleteRow
  ) {
    return (
      <BootstrapTable
        responsive
        striped
        className={classNames("table-question", {
          "sticky-first-col": isDynamicTable,
          "sticky-last-col": isDynamicTable
        })}
      >
        <TableQuestionHead
          question={checkedQuestion}
          skipFirst={skipFirst}
          questionAnswer={questionAnswer}
          rows={rows}
        />
          <TableQuestionBody
            disabled={disabled}
            monitoringEditOnly={monitoringEditOnly}
            question={checkedQuestion}
            rows={rows}
            questionAnswer={questionAnswer}
            skipFirst={skipFirst}
            validation={this.props.validation}
            editRow={this.handleShowEditDialog}
            deleteRow={deleteRow}
            reorderQuestionRow={this.props.reorderQuestionRow}
            addQuestionAnswer={this.props.addQuestionAnswer}
            setQuestionAnswer={this.props.setQuestionAnswer}
            removeQuestionAnswer={this.props.removeQuestionAnswer}
            validateQuestionAnswer={this.props.validateQuestionAnswer}
            textExpanded={this.state.textExpanded}
          />     
      </BootstrapTable>
    );
  }
}

TableQuestion.propTypes = {
  disabled: PropTypes.bool,
  question: PropTypes.object,
  surveyTemplate: PropTypes.object,
  questionAnswer: PropTypes.object,
  surveyResult: PropTypes.object,
  addRow: PropTypes.func,
  importRows: PropTypes.func,
  beginLoading: PropTypes.func,
  endLoading: PropTypes.func,
  deleteRow: PropTypes.func,
  deleteRows: PropTypes.func,
  addQuestionAnswer: PropTypes.func,
  setQuestionAnswer: PropTypes.func,
  removeQuestionAnswer: PropTypes.func,
  reorderQuestionRow: PropTypes.func,
  validation: PropTypes.array,
  downloadCSVTemplate: PropTypes.func,
  intl: PropTypes.object,
  validateQuestionAnswer: PropTypes.func
};

export default injectIntl(TableQuestion);
